// Comment Generator for Student Progress Reports
// Generates meaningful feedback based on student performance and position

const commentThemes = {
  excellent: {
    themes: [
      "Outstanding performance with exceptional dedication and hard work",
      "Remarkable achievement showing consistent excellence in all subjects",
      "Exceptional student demonstrating outstanding commitment to learning",
      "Outstanding results reflecting excellent study habits and dedication",
      "Remarkable academic excellence with consistent outstanding performance"
    ],
    keywords: ["outstanding", "exceptional", "remarkable", "excellent", "dedication", "achievement", "commitment", "hard work", "study habits", "academic excellence"]
  },
  
  veryGood: {
    themes: [
      "Very good performance with strong commitment to studies",
      "Consistent good results showing dedication and hard work",
      "Strong academic performance with positive learning attitude",
      "Good student demonstrating solid understanding and effort",
      "Promising performance with consistent improvement and dedication"
    ],
    keywords: ["very good", "strong", "consistent", "dedication", "hard work", "positive attitude", "solid understanding", "effort", "promising", "improvement"]
  },
  
  good: {
    themes: [
      "Good performance with room for further improvement",
      "Satisfactory results showing potential for better achievement",
      "Decent performance with opportunities for academic growth",
      "Good student with capacity to achieve higher results",
      "Steady performance with encouragement for continued effort"
    ],
    keywords: ["good", "room for improvement", "satisfactory", "potential", "achievement", "academic growth", "capacity", "steady", "encouragement", "effort"]
  },
  
  average: {
    themes: [
      "Average performance requiring more focused study and dedication",
      "Satisfactory results with need for increased academic effort",
      "Moderate performance showing potential for significant improvement",
      "Average student with opportunities to enhance academic skills",
      "Steady progress with encouragement for greater academic commitment"
    ],
    keywords: ["average", "focused study", "dedication", "satisfactory", "academic effort", "moderate", "improvement", "enhance", "academic skills", "commitment"]
  },
  
  belowAverage: {
    themes: [
      "Below average performance requiring immediate academic attention",
      "Needs significant improvement in study habits and dedication",
      "Academic performance below expectations requiring extra effort",
      "Student needs more focused approach to achieve better results",
      "Below par performance with potential for improvement through hard work"
    ],
    keywords: ["below average", "immediate attention", "significant improvement", "study habits", "dedication", "expectations", "extra effort", "focused approach", "below par", "hard work"]
  },
  
  poor: {
    themes: [
      "Poor performance requiring urgent intervention and support",
      "Unsatisfactory results needing immediate academic improvement",
      "Below standard performance requiring comprehensive study plan",
      "Poor student performance demanding immediate attention and effort",
      "Inadequate results requiring urgent remedial measures and support"
    ],
    keywords: ["poor", "urgent intervention", "support", "unsatisfactory", "immediate improvement", "below standard", "comprehensive study plan", "attention", "effort", "remedial measures"]
  },
  
  topPerformer: {
    themes: [
      "Exceptional top performer setting exemplary standards for peers",
      "Outstanding achievement placing among the best in class",
      "Top position holder demonstrating academic excellence and leadership",
      "Exceptional student achieving remarkable success in all subjects",
      "Outstanding performer serving as inspiration for other students"
    ],
    keywords: ["exceptional", "top performer", "exemplary", "standards", "peers", "outstanding", "best", "leadership", "academic excellence", "inspiration"]
  },
  
  consistentPerformer: {
    themes: [
      "Consistent performer maintaining steady academic progress throughout",
      "Reliable student showing consistent dedication to academic excellence",
      "Steady achiever demonstrating consistent effort and improvement",
      "Consistent performer with reliable academic performance and attitude",
      "Dependable student maintaining consistent academic standards and effort"
    ],
    keywords: ["consistent", "steady", "reliable", "dedication", "academic excellence", "achiever", "effort", "improvement", "dependable", "standards"]
  },
  
  improvingStudent: {
    themes: [
      "Student showing remarkable improvement in academic performance",
      "Encouraging progress with significant academic development and growth",
      "Improving student demonstrating positive academic transformation",
      "Notable improvement in academic performance and study habits",
      "Student showing promising academic progress and development"
    ],
    keywords: ["improvement", "remarkable", "encouraging", "progress", "development", "growth", "transformation", "notable", "study habits", "promising"]
  },
  
  strugglingStudent: {
    themes: [
      "Student facing academic challenges requiring immediate support and guidance",
      "Academic difficulties needing comprehensive intervention and assistance",
      "Student struggling with studies requiring extra attention and support",
      "Academic challenges demanding immediate remedial action and care",
      "Student in need of academic support and personalized learning approach"
    ],
    keywords: ["challenges", "immediate support", "guidance", "difficulties", "intervention", "assistance", "struggling", "attention", "remedial action", "personalized learning"]
  },
  
  creativeStudent: {
    themes: [
      "Creative student showing innovative thinking and artistic abilities",
      "Imaginative learner demonstrating creative problem-solving skills",
      "Artistic student with creative talents and innovative approaches",
      "Creative thinker showing unique perspectives and artistic flair",
      "Innovative student demonstrating creative abilities and artistic skills"
    ],
    keywords: ["creative", "innovative", "thinking", "artistic", "abilities", "imaginative", "problem-solving", "talents", "unique perspectives", "artistic flair"]
  },
  
  leadershipQualities: {
    themes: [
      "Student showing strong leadership qualities and peer guidance abilities",
      "Natural leader demonstrating excellent teamwork and communication skills",
      "Leadership potential with ability to guide and inspire fellow students",
      "Student with leadership qualities showing initiative and responsibility",
      "Emerging leader demonstrating strong character and peer influence"
    ],
    keywords: ["leadership", "qualities", "peer guidance", "natural leader", "teamwork", "communication", "potential", "initiative", "responsibility", "character"]
  },
  
  hardworkingStudent: {
    themes: [
      "Hardworking student demonstrating exceptional dedication and perseverance",
      "Diligent learner showing consistent effort and commitment to studies",
      "Industrious student with strong work ethic and academic determination",
      "Persistent learner demonstrating unwavering commitment to academic success",
      "Dedicated student showing remarkable work ethic and study discipline"
    ],
    keywords: ["hardworking", "dedication", "perseverance", "diligent", "effort", "commitment", "industrious", "work ethic", "determination", "persistent"]
  },
  
  socialSkills: {
    themes: [
      "Student with excellent social skills and positive peer relationships",
      "Socially adept learner showing good communication and interpersonal skills",
      "Student demonstrating strong social awareness and peer interaction abilities",
      "Socially skilled student with positive attitude and good relationships",
      "Student showing excellent social development and peer cooperation skills"
    ],
    keywords: ["social skills", "peer relationships", "socially adept", "communication", "interpersonal", "social awareness", "interaction", "positive attitude", "social development", "cooperation"]
  },
  
  potentialForGrowth: {
    themes: [
      "Student with immense potential for academic growth and development",
      "Promising learner showing great capacity for future academic success",
      "Student with untapped potential ready for academic advancement",
      "High-potential student demonstrating readiness for academic challenges",
      "Student showing excellent potential for future academic achievements"
    ],
    keywords: ["potential", "growth", "development", "promising", "capacity", "future success", "untapped", "advancement", "high-potential", "achievements"]
  }
};

// Function to determine student performance category based on position and percentage
const getPerformanceCategory = (position, totalStudents, percentage, gpa) => {
  const positionPercentage = (position / totalStudents) * 100;
  
  if (position <= 3 && percentage >= 90) return 'topPerformer';
  if (position <= 5 && percentage >= 85) return 'excellent';
  if (position <= 10 && percentage >= 80) return 'veryGood';
  if (position <= 15 && percentage >= 70) return 'good';
  if (position <= 25 && percentage >= 60) return 'average';
  if (position <= 35 && percentage >= 50) return 'belowAverage';
  if (percentage < 50) return 'poor';
  if (position <= 10) return 'consistentPerformer';
  if (percentage >= 75) return 'improvingStudent';
  if (percentage < 60) return 'strugglingStudent';
  
  return 'good'; // Default fallback
};

// Function to generate random comment based on performance category
const generateComment = (position, totalStudents, percentage, gpa, studentName) => {
  const category = getPerformanceCategory(position, totalStudents, percentage, gpa);
  const themeData = commentThemes[category];
  
  if (!themeData) {
    return "Student shows potential for academic improvement with dedicated effort.";
  }
  
  // Select random theme
  const randomTheme = themeData.themes[Math.floor(Math.random() * themeData.themes.length)];
  
  // Add student name for personalization
  const personalizedComment = randomTheme.replace(/Student|student/g, studentName || 'Student');
  
  return personalizedComment;
};

// Function to get additional traits based on performance
const getAdditionalTraits = (position, totalStudents, percentage) => {
  const traits = [];
  
  if (position <= 5) {
    traits.push('leadershipQualities');
  }
  if (percentage >= 85) {
    traits.push('hardworkingStudent');
  }
  if (position <= 10 && percentage >= 80) {
    traits.push('consistentPerformer');
  }
  if (percentage >= 70 && position <= 15) {
    traits.push('socialSkills');
  }
  if (percentage >= 60) {
    traits.push('potentialForGrowth');
  }
  
  return traits;
};

// Main export function
export const generateStudentComment = (studentData) => {
  const {
    position = 0,
    totalStudents = 1,
    percentage = 0,
    gpa = 0,
    studentName = 'Student',
    additionalTraits = []
  } = studentData;
  
  // Generate main comment
  const mainComment = generateComment(position, totalStudents, percentage, gpa, studentName);
  
  // Get additional traits
  const traits = getAdditionalTraits(position, totalStudents, percentage);
  
  // Generate additional trait comments if any
  let additionalComments = [];
  traits.forEach(trait => {
    if (commentThemes[trait]) {
      const randomTraitComment = commentThemes[trait].themes[Math.floor(Math.random() * commentThemes[trait].themes.length)];
      additionalComments.push(randomTraitComment);
    }
  });
  
  // Combine comments
  let finalComment = mainComment;
  if (additionalComments.length > 0) {
    const additionalComment = additionalComments[Math.floor(Math.random() * additionalComments.length)];
    finalComment += ` ${additionalComment}`;
  }
  
  // Ensure comment is exactly 10-12 words
  const words = finalComment.split(' ');
  if (words.length > 12) {
    finalComment = words.slice(0, 12).join(' ');
  } else if (words.length < 10) {
    // Add a few more words if too short
    const additionalWords = ["and", "shows", "great", "potential", "for", "success"];
    const needed = 10 - words.length;
    const extraWords = additionalWords.slice(0, needed);
    finalComment = words.concat(extraWords).join(' ');
  }
  
  return finalComment;
};

export default generateStudentComment;
